import React, { useMemo, useState } from "react";
import "./ClinicalReviewCollaborationPanel.css";

export type ReviewerPresence = {
  userId: string;
  displayName: string;
  role?: string;
  lastSeenAt: string;
  active: boolean;
};

export type ConsensusStatus =
  | "not_started"
  | "in_review"
  | "needs_consensus"
  | "consensus_reached"
  | "ready_for_report";

export type ClinicalReviewCollaborationPanelProps = {
  reviewers: ReviewerPresence[];
  consensusStatus: ConsensusStatus;
  onConsensusStatusChange: (status: ConsensusStatus) => void;
  handoffNote: string;
  onHandoffNoteChange: (note: string) => void;
  auditSummaries: Array<{ id: string; summary: string; createdAt: string }>;
  supportEscalationHref?: string;
  reviewQueueHref?: string;
};

const CONSENSUS_OPTIONS: Array<{ value: ConsensusStatus; label: string }> = [
  { value: "not_started", label: "Not started" },
  { value: "in_review", label: "In review" },
  { value: "needs_consensus", label: "Needs consensus" },
  { value: "consensus_reached", label: "Consensus reached" },
  { value: "ready_for_report", label: "Ready for report" },
];

export default function ClinicalReviewCollaborationPanel({
  reviewers,
  consensusStatus,
  onConsensusStatusChange,
  handoffNote,
  onHandoffNoteChange,
  auditSummaries,
  supportEscalationHref,
  reviewQueueHref,
}: ClinicalReviewCollaborationPanelProps) {
  const [draft, setDraft] = useState(handoffNote);
  const activeReviewers = useMemo(
    () => reviewers.filter((reviewer) => reviewer.active),
    [reviewers],
  );

  return (
    <div
      className="clinical-review-collab"
      data-testid="clinical-review-collaboration-panel"
    >
      <section aria-label="Reviewer presence">
        <h4>Reviewers</h4>
        <ul className="clinical-review-collab__reviewers">
          {reviewers.map((reviewer) => (
            <li
              key={reviewer.userId}
              className={reviewer.active ? "is-active" : ""}
            >
              <span>{reviewer.displayName}</span>
              <span>{reviewer.role || "reviewer"}</span>
              <span>{reviewer.active ? "present" : "away"}</span>
            </li>
          ))}
          {!reviewers.length && <li>No reviewers assigned.</li>}
        </ul>
        <p className="clinical-review-collab__meta">
          {activeReviewers.length} active now
        </p>
      </section>

      <section aria-label="Consensus status">
        <h4>Consensus status</h4>
        <select
          value={consensusStatus}
          aria-label="Consensus status selector"
          onChange={(event) =>
            onConsensusStatusChange(event.target.value as ConsensusStatus)
          }
        >
          {CONSENSUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </section>

      <section aria-label="Handoff note editor">
        <h4>Handoff note</h4>
        <textarea
          value={draft}
          aria-label="Handoff note"
          placeholder="PHI-safe handoff context for the next reviewer…"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => onHandoffNoteChange(draft)}
        />
      </section>

      <section aria-label="PHI-safe audit summaries">
        <h4>Audit summaries</h4>
        <ul>
          {auditSummaries.map((item) => (
            <li key={item.id}>
              <time dateTime={item.createdAt}>{item.createdAt}</time>
              <span>{item.summary}</span>
            </li>
          ))}
          {!auditSummaries.length && <li>No audit summaries yet.</li>}
        </ul>
      </section>

      <div className="clinical-review-collab__links">
        {reviewQueueHref && (
          <a href={reviewQueueHref}>Open report review queue</a>
        )}
        {supportEscalationHref && (
          <a href={supportEscalationHref}>Support escalation</a>
        )}
      </div>
    </div>
  );
}
